/*
 * Back9 quiz — spreadsheet logger.
 *
 * Posts one row per quiz event to a Google Apps Script web app, which appends
 * it to a spreadsheet. That is the whole architecture: no server to rent, no
 * database to run, and the merchant reads the data in a tool he already knows.
 *
 * This is deliberately the cheap option, and it behaves like one. Delivery is
 * fire-and-forget — a browser cannot read a response from an Apps Script web
 * app without tripping CORS, so a dropped row is dropped silently. That is an
 * acceptable trade for marketing counts and would not be for anything that had
 * to balance.
 *
 * What goes in a row: a timestamp, a random per-session id, and the answer keys
 * the definition already uses. What never goes in a row: name, email, phone,
 * address, customer id, IP, user agent, URL, referrer, UTM value, or any free
 * text. The session id is random, lives only in this browser, and is never put
 * in a URL or an HTML attribute.
 *
 * The answers themselves are about how a man's feet feel after eighteen holes,
 * which is health-adjacent whatever else it is. The rows are pseudonymous and
 * they are the merchant's own data about his own visitors, but that is a fact
 * worth stating plainly rather than burying: this file writes it down.
 */
(() => {
  'use strict';

  if (window.Back9QuizSheet) return;

  // Only an Apps Script web-app /exec endpoint. Not a general webhook sender:
  // a mistyped setting should fail closed rather than post a visitor's answers
  // to whatever host happened to be pasted into the theme editor.
  const ENDPOINT = /^https:\/\/script\.google\.com\/macros\/s\/[A-Za-z0-9_-]{20,}\/exec$/;

  const SESSION_KEY = 'back9-quiz-sheet-session';
  const SESSION_TTL_MS = 6 * 60 * 60 * 1000;

  const store = (() => {
    try {
      const probe = '__back9_sheet_probe__';
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return window.localStorage;
    } catch (error) {
      return null;
    }
  })();

  // 8 random bytes as hex. Enough to keep two sessions apart in a spreadsheet,
  // far too little to be an identity, and generated in the browser so it is
  // never derived from anything about the person.
  const newSessionId = () => {
    const bytes = new Uint8Array(8);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  };

  // One id for the whole run, so a refresh mid-quiz does not look like two
  // people. It expires on its own so a shared phone does not join up sessions
  // days apart.
  const sessionId = () => {
    const now = Date.now();
    if (store) {
      try {
        const saved = JSON.parse(store.getItem(SESSION_KEY) || 'null');
        if (saved && typeof saved.id === 'string' && now - saved.t < SESSION_TTL_MS) {
          store.setItem(SESSION_KEY, JSON.stringify({ id: saved.id, t: now }));
          return saved.id;
        }
      } catch (error) {
        /* fall through and mint a new one */
      }
    }
    const id = newSessionId();
    try {
      store?.setItem(SESSION_KEY, JSON.stringify({ id, t: now }));
    } catch (error) {
      /* a session that cannot be remembered is still worth sending */
    }
    return id;
  };

  // How long an event will wait for consent to resolve before being abandoned.
  // Shopify's API is not on the page at load, so the answer to "may I log this"
  // simply does not exist yet when the first event fires.
  const CONSENT_WAIT_MS = 8000;

  // Shopify's own consent state. Loaded rather than assumed: the API is not on
  // the page until it is asked for.
  const consent = () => {
    const state = { resolved: false, analytics: false, waiting: [] };

    const settle = () => {
      if (!state.resolved) return;
      const waiting = state.waiting.splice(0);
      waiting.forEach((fn) => {
        try {
          fn();
        } catch (error) {
          /* one bad waiter must not strand the rest */
        }
      });
    };

    state.whenResolved = (fn) => {
      if (state.resolved) fn();
      else state.waiting.push(fn);
    };

    // Shopify's own analyticsProcessingAllowed() is the question actually being
    // asked, and it answers it for both configurations: it is true when the
    // visitor consented, and also true where no banner is required at all. The
    // raw consent string cannot tell those apart — an empty string means both
    // "not asked" and "never needs asking" — so reading it directly silently
    // recorded nothing on a store that had turned its banner off.
    const read = () => {
      const privacy = window.Shopify?.customerPrivacy;
      if (!privacy) return;

      try {
        if (typeof privacy.analyticsProcessingAllowed === 'function') {
          state.resolved = true;
          state.analytics = privacy.analyticsProcessingAllowed() === true;
          return;
        }
        const current = privacy.currentVisitorConsent?.();
        if (!current) return;
        state.resolved = true;
        state.analytics = current.analytics === true || current.analytics === 'yes';
      } catch (error) {
        /* leave it unresolved, which counts as "no" */
      } finally {
        settle();
      }
    };

    read();
    try {
      if (!state.resolved && window.Shopify?.loadFeatures) {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => read());
      }
    } catch (error) {
      /* nothing to do */
    }
    document.addEventListener('visitorConsentCollected', read);

    // If the answer never arrives, stop holding events forever. Unresolved
    // stays "no", so the queue is released to be discarded rather than sent.
    window.setTimeout(() => {
      if (state.resolved) return;
      state.resolved = true;
      settle();
    }, CONSENT_WAIT_MS);

    return state;
  };

  // text/plain keeps this a "simple" request, so the browser sends it without a
  // preflight — which an Apps Script endpoint would fail. sendBeacon is used
  // where the page is about to go away, which is exactly the CTA click.
  const send = (url, payload, urgent) => {
    const body = JSON.stringify(payload);
    try {
      if (urgent && navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
        if (navigator.sendBeacon(url, blob)) return;
      }
      window.fetch(url, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body
      }).catch(() => {});
    } catch (error) {
      /* A logger must never be the reason a man cannot finish the quiz. */
    }
  };

  window.Back9QuizSheet = {
    create({ config } = {}) {
      const url = String(config?.sheetEndpoint || '').trim();
      if (!ENDPOINT.test(url)) return null;

      const gate = config?.sheetRespectConsent === false ? null : consent();
      const id = sessionId();
      const quiz = String(config?.quizKey || 'quiz');

      // Some events must happen at most once per session. `start` and
      // `complete` are the denominator and the numerator of the funnel, and a
      // reload re-fires both: `start` because the page booted again, `complete`
      // because resuming restores the result screen. Counting either twice
      // makes the funnel lie in a direction nobody would notice.
      const once = new Set();
      const ONCE_KEY = `back9-quiz-sheet-once:${id}`;
      try {
        JSON.parse(store?.getItem(ONCE_KEY) || '[]').forEach((e) => once.add(e));
      } catch (error) {
        /* an unreadable record just means nothing has been sent yet */
      }
      const remember = (event) => {
        once.add(event);
        try {
          store?.setItem(ONCE_KEY, JSON.stringify([...once]));
        } catch (error) {
          /* in-memory alone still stops a double within this page view */
        }
      };

      const row = (event, fields, urgent) => {
        if (event === 'start' || event === 'complete') {
          if (once.has(event)) return;
          remember(event);
        }

        const payload = Object.assign({
          v: 1,
          ts: new Date().toISOString(),
          quiz,
          session: id,
          event
        }, fields || {});

        // The event that matters most fires the moment the page loads, which is
        // before Shopify has answered whether logging is allowed. Dropping it
        // there cost every bounce in the funnel: a man who landed and left
        // without tapping anything left no trace at all, so the denominator was
        // only ever the people who had already engaged. Held, not dropped.
        if (!gate) {
          send(url, payload, urgent === true);
          return;
        }
        if (gate.resolved) {
          if (gate.analytics) send(url, payload, urgent === true);
          return;
        }
        gate.whenResolved(() => {
          if (gate.analytics) send(url, payload, urgent === true);
        });
      };

      return {
        start: () => row('start', {}),
        answer: (step, questionKey, answerKey, answerKeys) => row('answer', {
          step,
          question: questionKey,
          answer: answerKey,
          answers: Array.isArray(answerKeys) && answerKeys.length > 1 ? answerKeys.join('|') : ''
        }),
        complete: (steps, outcomeKey) => row('complete', { step: steps, outcome: outcomeKey || '' }),
        cta: (steps, outcomeKey) => row('cta', { step: steps, outcome: outcomeKey || '' }, true)
      };
    }
  };
})();
